import React, { useMemo } from 'react';
import { ArrowRight, Maximize2, X } from 'lucide-react';
import type { EnterpriseAction } from '../../../services/actionService';
import type { LstLideradoAderencia, LstRotinaDoDia } from '../types';
import { computeTplSituationalCockpit } from '../services/tplSituationalCockpit';
import { buildDataflowExecutiveRibbon } from '../services/dataflowExecutiveDiagnostics';
import { computeDataflowInsights } from '../services/dataflowInsightEngine';
import { shouldShowCount } from '../services/tplSeverityStyles';
import { TplAttentionNowBlock } from './tpl/ux/TplAttentionNowBlock';
import { DataflowExecutiveRibbon } from './dataflow/DataflowExecutiveRibbon';
import { DataflowInsightEngine } from './dataflow/DataflowInsightEngine';

interface LstKpiDrawerProps {
  open: boolean;
  onClose: () => void;
  onExpand: () => void;
  cargoLabel: string;
  today: string;
  rotinas: LstRotinaDoDia[];
  chainRows: LstLideradoAderencia[];
  correctiveActions: EnterpriseAction[];
  loading: boolean;
}

/**
 * Nível 0 — Cockpit do turno (Consciência Situacional).
 * Responde: o que está acontecendo agora e o que exige ação imediata.
 */
const EMPTY_EXECUTION = {
  planned: 0,
  completed: 0,
  pending: 0,
  deviations: 0,
  losses: 0,
  disciplinePct: 0,
};

export const LstKpiDrawer: React.FC<LstKpiDrawerProps> = ({
  open,
  onClose,
  onExpand,
  cargoLabel,
  today,
  rotinas = [],
  chainRows = [],
  correctiveActions = [],
  loading,
}) => {
  const cockpit = useMemo(() => {
    if (!open) {
      return { execution: EMPTY_EXECUTION, attentionNow: [] };
    }
    return computeTplSituationalCockpit({
      today: today ?? '',
      rotinas,
      chainRows,
      correctiveActions,
      selfLabel: cargoLabel,
    });
  }, [open, today, rotinas, chainRows, correctiveActions, cargoLabel]);

  const ribbon = useMemo(() => {
    if (!open) return null;
    const overdue = correctiveActions.filter(
      (a) =>
        a.status !== 'completed' &&
        a.status !== 'cancelled' &&
        a.due_date &&
        today &&
        a.due_date < today,
    ).length;
    return buildDataflowExecutiveRibbon({
      ielScore: cockpit.execution.disciplinePct,
      maturityScore: cockpit.execution.disciplinePct,
      disciplinaPct: cockpit.execution.disciplinePct,
      riskCount: cockpit.attentionNow.length,
      lossCount: cockpit.execution.losses,
      actionCount: overdue,
      escalationsOverdue: overdue,
    });
  }, [open, cockpit, correctiveActions, today]);

  const insights = useMemo(() => {
    if (!open) return [];
    return computeDataflowInsights({
      disciplinaPct: cockpit.execution.disciplinePct,
      ielScore: cockpit.execution.disciplinePct,
      topLossLabels: cockpit.attentionNow.map((e) => e.subject),
      topLossCounts: cockpit.attentionNow.map(() => 1),
      totalLosses: cockpit.execution.losses,
      mainRiskLabel: cockpit.attentionNow[0]?.detail ?? null,
    });
  }, [open, cockpit]);

  if (!open) return null;

  const { execution, attentionNow } = cockpit;

  return (
    <div className="fixed inset-0 z-[9998] flex justify-end">
      <button type="button" aria-label="Fechar" onClick={onClose} className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]" />

      <aside
        role="dialog"
        aria-modal="true"
        className="relative flex h-full w-full max-w-[420px] flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950"
        style={{ animation: 'slideInRight .18s ease-out' }}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div>
            <h3 className="text-[14px] font-bold text-slate-800 dark:text-slate-100">
              Cockpit do Turno
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">{cargoLabel} · hoje</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
            title="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="shrink-0 border-b border-slate-200 px-4 py-2.5 dark:border-slate-800">
          <button
            type="button"
            onClick={onExpand}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[12px] font-semibold text-blue-700 transition-colors hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-300"
          >
            <Maximize2 size={14} /> Por quê? — Análise Completa
            <ArrowRight size={12} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {loading ? (
            <p className="py-10 text-center text-[12px] text-slate-400">Carregando cockpit…</p>
          ) : (
            <>
              {ribbon && <DataflowExecutiveRibbon data={ribbon} compact />}
              <DataflowInsightEngine insights={insights} compact />

              <section>
                <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  Execução hoje
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <CockpitStat label="Rotinas previstas" value={execution.planned} always />
                  <CockpitStat label="Rotinas realizadas" value={execution.completed} always />
                  <CockpitStat label="Pendências" value={execution.pending} highlight={execution.pending > 0} />
                  <CockpitStat label="Desvios" value={execution.deviations} highlight={execution.deviations > 0} />
                  {shouldShowCount(execution.losses) && (
                    <CockpitStat label="Perdas" value={execution.losses} tone="red" />
                  )}
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                  <div
                    className={`h-full rounded-full transition-all ${
                      execution.pending > 0 ? 'bg-amber-400' : 'bg-emerald-500'
                    }`}
                    style={{
                      width: `${execution.planned > 0 ? Math.round((execution.completed / execution.planned) * 100) : 0}%`,
                    }}
                  />
                </div>
                <p className="mt-1 text-[10px] text-slate-400">
                  Comparecimento: {execution.disciplinePct}%
                </p>
              </section>

              <TplAttentionNowBlock events={attentionNow} />
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

function CockpitStat({
  label,
  value,
  always = false,
  highlight = false,
  tone,
}: {
  label: string;
  value: number;
  always?: boolean;
  highlight?: boolean;
  tone?: 'red';
}) {
  if (!always && !shouldShowCount(value)) return null;

  return (
    <div
      className={`rounded-lg border px-2.5 py-2 ${
        highlight
          ? 'border-amber-200 bg-amber-50/50 dark:border-amber-900/40 dark:bg-amber-950/15'
          : tone === 'red'
            ? 'border-red-200 bg-red-50/50 dark:border-red-900/40 dark:bg-red-950/15'
            : 'border-slate-200 dark:border-slate-800'
      }`}
    >
      <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p
        className={`text-[20px] font-bold tabular-nums leading-none ${
          tone === 'red'
            ? 'text-red-600'
            : highlight
              ? 'text-amber-600'
              : 'text-slate-800 dark:text-slate-100'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

export default LstKpiDrawer;
